import { getCountryByCode } from "../../../../utils/Helper";
import { validateDesignation } from "../../../../utils/designation";
import { isValidEmail } from "./addressFields";
import {
  liveSectionRows,
  mergeSectionDrafts,
  sectionRecordOf,
  sectionRowKey,
} from "./sectionDrafts";

/* Authorized Signatory and Key Personnel — the rules they share. */

/* Transcribed row order, which the design also uses. Nationality is present
   only for a signatory: KeyPersonnel.jsx lists it in its whitelist but has NO
   icon for it, and the design draws four rows there — see FINDINGS F-063. */
export const PERSON_ROWS = [
  { key: "name", label: "Name" },
  { key: "designation", label: "Designation" },
  { key: "work_email", label: "Work email" },
  { key: "work_mobile", label: "Work phone" },
  { key: "nationality", label: "Nationality", nationalityOnly: true },
];

export const personRows = (spec) =>
  PERSON_ROWS.filter((row) => !row.nationalityOnly || spec?.withNationality);

/* Transcribed: a work phone is stored "<dial>|<number>" and the card strips the pipe. */
export const personRowValues = (record = {}) => ({
  name: record?.name || "",
  designation: record?.designation || "",
  work_email: record?.work_email || "",
  work_mobile: String(record?.work_mobile ?? "").replace("|", ""),
  nationality: record?.nationality
    ? getCountryByCode(record.nationality) || record.nationality
    : "",
});

/* Transcribed: "raised" and "pending" join "Requested" as awaiting review. */
export const personStatus = (record) =>
  ["raised", "pending"].includes(record?.status) ? "Requested" : record?.status;

/* Transcribed: only undeleted rows are listed. */
/* The merge and its two helpers are the flow-wide ones — six live sections
   performed the same twenty lines. See sectionDrafts.js. */
export const livePeople = liveSectionRows;
export const personOf = sectionRecordOf;
export const mergePeopleDrafts = mergeSectionDrafts;
export const personKey = sectionRowKey;

/* Transcribed validation, message for message. The designation rule is the
 * SHARED `validateDesignation` — the same one every job-title field in the app
 * uses — rather than a fourth copy of its character rules.
 */
export const validatePerson = ({ name, designation, email, phone, nationality }, spec) => {
  const errors = {};

  if (String(name ?? "").trim() === "") errors.name = "Full Name is required.";

  const designationMessage = validateDesignation(designation);
  if (designationMessage) errors.designation = designationMessage;

  if (String(email ?? "").trim() === "") {
    errors.email = "Email Address is required.";
  } else if (!isValidEmail(String(email).trim())) {
    errors.email = "Invalid Email Address.";
  }

  if (String(phone ?? "").trim() === "") errors.phone = "Phone Number is required.";

  /* Transcribed: only the signatory form asks for one. */
  if (spec?.withNationality && String(nationality ?? "").trim() === "") {
    errors.nationality = "Nationality is required.";
  }

  return errors;
};

/* The four (or five) fields every save sends. */
export const personPayload = ({ name, designation, email, phone, nationality }, spec) => ({
  name,
  designation,
  work_email: email,
  work_mobile: phone,
  ...(spec?.withNationality ? { nationality } : {}),
});

/* Which of the three writes a submit performs — the same chain as every other
   section on this flow, resolved against this entity's own draft keys. */
export const personSavePath = ({ verifiedCompany, editing }, spec) => {
  const verified =
    verifiedCompany !== null &&
    verifiedCompany !== undefined &&
    typeof verifiedCompany === "object" &&
    Object.keys(verifiedCompany).length !== 0;

  if (verified) {
    return editing?.[spec.draftIdKey] ? "updateDraft" : "createDraft";
  }
  return editing ? "update" : "create";
};
